import { Calendar } from 'lucide-react';
import { DatePicker } from '@/components/ui/DatePicker';

/**
 * Which table the list shows. Homework and class activities are the two kinds
 * of work a teacher sets; there is no third kind to filter on, so the tabs are
 * the two tables and their sum.
 */
export const HOMEWORK_CATEGORIES = ['All', 'Homework', 'Activities', 'Completed Homework'] as const;
export type HomeworkCategory = (typeof HOMEWORK_CATEGORIES)[number];

/** The quick day choices above the list; `CUSTOM` means the date picker's day. */
export const QUICK_DATE_FILTERS = ['ALL', 'TODAY', 'YESTERDAY', 'CUSTOM'] as const;
export type QuickDateFilter = (typeof QUICK_DATE_FILTERS)[number];

interface HomeworkFilterBarProps {
  selectedCategory: HomeworkCategory;
  onSelectCategory: (cat: HomeworkCategory) => void;
  /** False when the school has class activities switched off — the tab is not offered. */
  showActivities: boolean;
  /** The subjects the open tab has work in. `ALL` is the first pill. */
  subjectPills: string[];
  selectedSubject: string;
  onSubjectChange: (subject: string) => void;
  quickDateFilter: QuickDateFilter;
  onSelectQuickDate: (dOpt: QuickDateFilter) => void;
  customDate: string;
  onCustomDateChange: (date: string) => void;
}

export function HomeworkFilterBar({
  selectedCategory,
  onSelectCategory,
  showActivities,
  subjectPills,
  selectedSubject,
  onSubjectChange,
  quickDateFilter,
  onSelectQuickDate,
  customDate,
  onCustomDateChange,
}: HomeworkFilterBarProps) {
  const categories: HomeworkCategory[] = showActivities
    ? ['All', 'Homework', 'Activities', 'Completed Homework']
    : ['All', 'Homework', 'Completed Homework'];

  return (
    <div className="flex flex-col gap-2.5">
      <div className="rounded-2xl bg-white dark:bg-[#1b1d34] border border-slate-200/80 dark:border-white/10 px-2.5 py-1.5  shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-3">
        {/* Category Horizontal Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto [::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {categories.map((cat) => {
            const isActive = selectedCategory === cat;
            return (
              <button
                key={cat}
                type="button"
                aria-pressed={isActive}
                onClick={() => onSelectCategory(cat)}
                className={`px-4 py-1 rounded-full text-xs sm:text-base font-bold whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  isActive
                    ? 'bg-indigo-600 text-white shadow-sm shadow-indigo-200 dark:shadow-none'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/5'
                }`}
              >
                {cat}
              </button>
            );
          })}
        </div>

        {/* Date Quick Filters & Custom Picker */}
        <div className="flex flex-wrap items-center gap-2 shrink-0 self-start md:self-auto">
          <span className="text-sm font-bold text-slate-500 dark:text-slate-400 flex items-center gap-1.5 mr-0.5">
            <Calendar className="h-3.5 w-3.5 text-indigo-500" /> Date:
          </span>

          <button
            type="button"
            onClick={() => onSelectQuickDate(quickDateFilter === 'TODAY' ? 'ALL' : 'TODAY')}
            className={`px-4 py-1.5 rounded-full text-sm font-bold transition-all duration-200 cursor-pointer ${
              quickDateFilter === 'TODAY' && !customDate
                ? 'bg-indigo-600 text-white shadow-2xs'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
            }`}
          >
            Today
          </button>

          <button
            type="button"
            onClick={() => onSelectQuickDate(quickDateFilter === 'YESTERDAY' ? 'ALL' : 'YESTERDAY')}
            className={`px-4 py-1.5 rounded-full text-sm font-bold transition-all duration-200 cursor-pointer ${
              quickDateFilter === 'YESTERDAY' && !customDate
                ? 'bg-indigo-600 text-white shadow-2xs'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
            }`}
          >
            Yesterday
          </button>

          <DatePicker
            label="Filter homework by date"
            hideLabel
            size="sm"
            isClearable
            placeholder="Pick a date"
            value={customDate}
            onChange={(e) => onCustomDateChange(e.target.value)}
            className="w-36 sm:w-40 rounded-full"
          />
        </div>
      </div>

      {/* Subjects as pills, always in view — a dropdown tucked beside the sort
          was easy to miss. Only the subjects the open tab has work in. */}
      {subjectPills.length > 1 && (
        <div
          role="group"
          aria-label="Filter by subject"
          className="flex items-center gap-1.5 overflow-x-auto [::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
        >
          {subjectPills.map((subject) => {
            const isActive = selectedSubject === subject;
            return (
              <button
                key={subject}
                type="button"
                aria-pressed={isActive}
                onClick={() => onSubjectChange(subject)}
                className={`px-3.5 py-1 rounded-full text-xs sm:text-sm font-bold whitespace-nowrap border transition-colors cursor-pointer ${
                  isActive
                    ? 'bg-indigo-600 border-indigo-600 text-white'
                    : 'bg-white dark:bg-[#1b1d34] border-slate-200/80 dark:border-white/10 text-slate-600 dark:text-slate-300 hover:border-indigo-300'
                }`}
              >
                {subject === 'ALL' ? 'All subjects' : subject}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
